import {FC, useMemo} from 'react'
import {Autocomplete, TextField} from '@mui/material'
import {useToast} from '../../../../../_fds/layout/components/toast-messages'
import {useStore} from '../../store'
import {updateShift} from '../../core/_requests'
import {getBusinessDay, getPositionDisplayName, toTitleCase} from '../../helpers/generals'
import {ProcessedEvent} from '../../types'
import {BaseEmployeeModel} from '../../core/_models'
import {useAuth, hasPermission, LABOR_SCHEDULING_PERMS} from '../../../auth'
import {AUTOCOMPLETE_PAPER_PROPS, AUTOCOMPLETE_SX} from './autocompleteSkin'

type AssignEmployeeSelectProps = {
  event: ProcessedEvent
}

type EmployeeOption = {
  id: number
  label: string
}

// Searchable employee picker shown in the leading cell of an unassigned
// shift's row. When the shift has a position, only employees of that position
// are offered. Picking one PUTs the shift and moves it onto the employee's
// row (the unassigned row disappears with the resource recompute).
const AssignEmployeeSelect: FC<AssignEmployeeSelectProps> = ({event}) => {
  const {
    employees,
    events,
    handleState,
    selectedLocationId,
    selectedLaborScheduleId,
    hoursOfOperations,
    triggerLoading,
  } = useStore()
  const {showToast} = useToast()
  const {currentUser} = useAuth()
  // Assigning an employee PUTs the shift — an edit. Without the permission,
  // show a plain read-only label instead of the picker.
  const canEditShifts = hasPermission(currentUser, LABOR_SCHEDULING_PERMS.edit)

  const options: EmployeeOption[] = useMemo(
    () =>
      employees
        .filter((e: BaseEmployeeModel) => e.is_active !== false && e.hide_in_schedule !== true)
        .filter((e: BaseEmployeeModel) => !event.position || e.position === event.position)
        .map((e: BaseEmployeeModel) => ({
          id: e.id,
          label: `${toTitleCase(e.first_name)} ${toTitleCase(e.last_name)}`,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employees, event.position]
  )

  const assign = async (employeeId: number) => {
    const businessDay = getBusinessDay(event.start, hoursOfOperations)
    const updatedEvent = {
      ...event,
      employee_id: employeeId,
      selectedLocationId: selectedLocationId,
      selectedLaborScheduleId: selectedLaborScheduleId,
      businessDay: businessDay,
    }
    try {
      triggerLoading(true)
      await updateShift(updatedEvent, businessDay)
      handleState(
        events.map((ev) =>
          ev.event_id === event.event_id ? {...ev, employee_id: employeeId} : ev
        ),
        'events'
      )
      showToast('Shift Assigned', 'Shift assigned successfully!', 'success')
    } catch (error) {
      showToast('Shift Assignment Failed', 'Unable to assign shift!', 'danger')
    } finally {
      triggerLoading(false)
    }
  }

  if (!canEditShifts) {
    return <span className='text-gray-600 fs-7 fw-bold'>Unassigned</span>
  }

  return (
    <Autocomplete
      size='small'
      options={options}
      getOptionLabel={(option) => option.label}
      // Controlled at null on purpose: a pick either assigns the shift (the
      // row disappears) or fails (the picker must read empty again) — the
      // selection itself is never state this component should hold on to.
      value={null}
      blurOnSelect
      onChange={(_e, option) => option && assign(option.id)}
      noOptionsText={
        event.position ? `No ${getPositionDisplayName(event.position)} employees` : 'No employees'
      }
      // Skinned with the app's Bootstrap CSS variables, shared with the
      // availability page's picker — see autocompleteSkin.ts.
      sx={{...AUTOCOMPLETE_SX, width: '100%'}}
      ListboxProps={{style: {maxHeight: 220}}}
      componentsProps={AUTOCOMPLETE_PAPER_PROPS}
      renderInput={(params) => (
        <TextField {...params} placeholder='Assign employee…' size='small' />
      )}
    />
  )
}

export {AssignEmployeeSelect}
